import {Collapse, DatePicker, Form, Select, Input} from 'antd';
import {FinancialInstrumentPicker} from 'components/financialInstrumentPicker';
import styles from './newNotification.module.scss';
import moment from 'moment';
import {
  audienceTypes,
  notificationFilterOptions,
  notificationLastVisitOptions,
  notificationOrderStatusOptions,
} from 'utils/constants';
import {
  InvestingExperienceEnum,
  investmentPreferenceEnum,
  investmentHorizonEnum,
  investmentAffiliationEnum,
  visaTypeEnum,
  citizenStatusEnum,
  annualNetIncomeEnum,
  taxFilingStatusEnum,
  employmentStatusEnum,
  estimatedNetWorthEnum,
  investmentGoalsEnum,
  investmentStrategiesEnum,
  kycStatusEnums,
} from 'utils/mock';
import {NotificationRecipientSelect} from 'components/notificationRecipientSelect/NotificationRecipientSelect';
import {getUserTimezone} from 'utils/helper';
import {useEffect} from 'react';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {GET_ALL_RISK_PROFILES} from 'graphql/queries/users';
import {WalletActivityFilter} from './WalletActivityFilter';
import {HoldingsFilter} from './HoldingsFilter';
import {supportsHoldings} from '../utils/holdingsAudience';
import {AUDIENCE_SECTIONS} from '../utils/audienceSummary';
import {SectionHeader, SectionRule} from '../components/AudienceReview';

// Yes / No / Either selects share these words.
const EITHER_HINT = 'Either leaves this filter off.';

const NotificationFilter = ({
  audience,
  setAudience,
  recipients,
  setRecipients,
  defaultUsers,
  allGroups,
  allCampaigns,
  form,
  allTopics,
  allCountries,
  isEditMode = false,
  getHoldingsPreviewInput,
  lookups,
}) => {
  const {Option} = Select;
  const width = 400;
  const signupDate = Form.useWatch('signupDate', form);

  // Watch duration unit to enable/disable value field
  const durationUnit = Form.useWatch('durationUnit', form);

  const {data: allRiskProfiles, loading: loadingRiskProfiles} = useQuery(
    GET_ALL_RISK_PROFILES,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error?.message),
    },
  );

  // Clear duration value when unit is cleared
  useEffect(() => {
    if (!durationUnit) {
      form.setFieldValue('durationValue', undefined);
    }
  }, [durationUnit, form]);

  const header = key => {
    const section = AUDIENCE_SECTIONS.find(item => item.key === key);
    return (
      <SectionHeader
        sectionKey={key}
        title={section.title}
        hint={section.hint}
        lookups={lookups}
      />
    );
  };
  const rule = key => (
    <SectionRule>
      {AUDIENCE_SECTIONS.find(item => item.key === key).rule}
    </SectionRule>
  );

  const yesNoField = (name, label, tooltip) => (
    <Form.Item
      className={styles['field-width']}
      label={label}
      name={name}
      tooltip={`${tooltip} ${EITHER_HINT}`}>
      <Select defaultValue={'EITHER'} placeholder="Either">
        {notificationFilterOptions.map(option => (
          <Option key={option.value}>
            <span>{option.label}</span>
          </Option>
        ))}
      </Select>
    </Form.Item>
  );

  // Every section stays open while editing so its saved values are on screen.
  const openKeys = isEditMode
    ? [
        'status',
        'investment',
        'account',
        'holdings',
        'location',
        'financial',
        'investor',
        'signup',
      ]
    : [];

  return (
    <div>
      <div className={styles['audience-grid']}>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Audience required',
            },
          ]}
          name="audience"
          label="Audience"
          tooltip="Who the notification starts from. Every filter below narrows this group; none of them adds people to it."
          className={styles['fwidth']}
          initialValue={'ALL_USERS'}>
          <Select
            name="audience"
            value={audience}
            placeholder="All Users"
            defaultValue="ALL_USERS"
            onChange={e => setAudience(e)}>
            {audienceTypes.map(data => (
              <Option key={data.value}>{data.label}</Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      {/* The recipient field carries no `name`: the recipient set lives in the
          composer's own state, not in the antd form. A named Form.Item would
          clone `value`/`onChange` onto the control and overwrite the ones
          passed here. */}
      {audience === 'USERS' && (
        <Form.Item
          className={styles['fwidth']}
          label="Recipients"
          tooltip="The exact people to notify. Search for users or upload a CSV of email addresses.">
          <NotificationRecipientSelect
            value={recipients}
            onChange={setRecipients}
            defaultUsers={defaultUsers}
          />
        </Form.Item>
      )}
      {audience === 'USERS_BY_GROUP' && (
        <Form.Item
          rules={[{required: audience === 'USERS_BY_GROUP'}]}
          label="Groups"
          tooltip="Members of any of these groups receive it."
          name="groups">
          <Select
            mode="tags"
            className={styles['fwidth']}
            placeholder="Select by Groups">
            {allGroups?.map(data => (
              <Option key={data?.id}>
                <div className={styles['name-data']}>
                  <img src={data?.bannerImg} alt="" />
                  <span>{data?.name}</span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {audience === 'INVESTORS_IN_A_CAMPAIGN' && (
        <Form.Item
          label="Live Campaign"
          name="liveCampaignId"
          tooltip="Users who invested in this live campaign.">
          <Select className={styles['fwidth']} placeholder="Select by Campaign">
            {allCampaigns
              ?.filter(
                data =>
                  data?.campaignMode === 'LIVE' &&
                  data?.campaignStatus !== 'DRAFT',
              )
              ?.map(data => (
                <Option key={data?.id}>
                  <div className={styles['option-data']}>
                    <div className={styles['name-data']}>
                      <img src={data?.fundraisingCompany?.logoImgURL} alt="" />
                      <span>{data?.fundraisingCompany?.companyName}</span>
                    </div>
                    <div>--{data?.campaignStatus}</div>
                  </div>
                </Option>
              ))}
          </Select>
        </Form.Item>
      )}
      {audience === 'INVESTORS_WITH_RESERVATIONS' && (
        <Form.Item
          label="Test Campaign"
          name="testCampaignId"
          tooltip="Users who reserved in this test campaign.">
          <Select className={styles['fwidth']} placeholder="Select by Campaign">
            {allCampaigns
              ?.filter(
                data =>
                  data?.campaignMode === 'TEST' &&
                  data?.campaignStatus !== 'DRAFT',
              )
              ?.map(data => (
                <Option key={data?.id}>
                  <div className={styles['option-data']}>
                    <div className={styles['name-data']}>
                      <img src={data?.fundraisingCompany?.logoImgURL} alt="" />
                      <span>{data?.fundraisingCompany?.companyName}</span>
                    </div>
                    <div>--{data?.campaignStatus}</div>
                  </div>
                </Option>
              ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item
        label="Topics"
        name="topics"
        tooltip="Tags the notification with these topics. Topics label the notification; they are not an audience filter.">
        <Select
          mode="multiple"
          className={styles['fwidth']}
          placeholder="Select by Topics">
          {allTopics?.map(item => (
            <Option key={`${item?.name};${item?.id}`}>
              <div className={styles['name-data']}>
                <img src={item?.icon} alt="" />
                <span>{item?.name}</span>
              </div>
            </Option>
          ))}
        </Select>
      </Form.Item>

      <h3 className={styles['filters-title']}>Narrow it down (optional)</h3>
      <p className={styles['filters-hint']}>
        Each section narrows the audience further: a user must match every
        section you fill in. You will see the full audience before it is sent.
      </p>

      <Collapse
        defaultActiveKey={openKeys}
        className={styles['filter-sections']}>
        {/* Specific users are already picked by hand, so status filters do
            not apply to them. */}
        {audience !== 'USERS' && (
          <Collapse.Panel key="status" header={header('status')}>
            {rule('status')}
            <div className={styles.accredited}>
              {yesNoField(
                'accredited',
                'Accredited',
                'Yes: only accredited investors. No: only users who are not.',
              )}
              {yesNoField(
                'activeInvestors',
                'Active Investors',
                'Yes: only users who invest actively. No: only users who do not.',
              )}
              {yesNoField(
                'verifiedInvestors',
                'Verified Investors',
                'Yes: only verified investors. No: only users who are not verified.',
              )}
              <Form.Item
                className={styles['field-width']}
                label="Latest App Visit"
                name="latestVisit"
                tooltip={`Only users whose last app visit is older than this. ${EITHER_HINT}`}>
                <Select defaultValue={'EITHER'} placeholder="Either">
                  {notificationLastVisitOptions.map(option => (
                    <Option key={option.value}>
                      <span>{option.label}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                className={styles['field-width']}
                label="Profile Completion"
                name="profileCompletion"
                tooltip={`Only users who stopped at this onboarding step. ${EITHER_HINT}`}>
                <Select defaultValue={'EITHER'} placeholder="Either">
                  <Option key="EITHER">Either</Option>
                  <Option key="EmailVerification">
                    Email Verification - Basics
                  </Option>
                  <Option key="PhoneVerification">
                    Phone Verification - Basics
                  </Option>
                  <Option key="Basics">Step One(Not Completed) - Basics</Option>
                  <Option key="Preferences">
                    Step Two(Not Completed) - Preferences
                  </Option>
                  <Option key="Investments">
                    Step Three(Not Completed) - Investments
                  </Option>
                  <Option key="Customization">
                    Step Four(Not Completed) - Customization
                  </Option>
                </Select>
              </Form.Item>
              {yesNoField(
                'isDabaPro',
                'Daba Pro Members',
                'Yes: only Daba Pro subscribers. No: only users without Daba Pro.',
              )}
              <Form.Item
                name="isKycApproved"
                label="Identity Check Statuses"
                tooltip="Users whose identity check is in any of these statuses. Empty: any status."
                className={styles['field-width']}>
                <Select mode="multiple" placeholder="Any status" allowClear>
                  {kycStatusEnums.map(({label, value}) => (
                    <Option key={value}>{label}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="isDocumentCheckApproved"
                label="Document Check Statuses"
                tooltip="Users whose document check is in any of these statuses. Empty: any status."
                className={styles['field-width']}>
                <Select mode="multiple" placeholder="Any status" allowClear>
                  {kycStatusEnums.map(({label, value}) => (
                    <Option key={value}>{label}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="isAmlApproved"
                label="AML Check Statuses"
                tooltip="Users whose anti-money-laundering check is in any of these statuses. Empty: any status."
                className={styles['field-width']}>
                <Select mode="multiple" placeholder="Any status" allowClear>
                  {kycStatusEnums.map(({label, value}) => (
                    <Option key={value}>{label}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="riskProfile"
                className={styles['field-width']}
                label="Risk Profile"
                tooltip="Users with any of these risk profiles. Empty: any risk profile.">
                <Select
                  mode="multiple"
                  allowClear
                  loading={loadingRiskProfiles}
                  placeholder="Any risk profile"
                  showSearch
                  optionFilterProp="filter"
                  filterOption={false}>
                  {allRiskProfiles?.getAllRiskProfiles?.data?.map(data => (
                    <Option key={data.id}>{data.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </Collapse.Panel>
        )}
        <Collapse.Panel key="investment" header={header('investment')}>
          {rule('investment')}
          <NotificationOrderByFilter form={form} />
        </Collapse.Panel>
        <Collapse.Panel key="account" header={header('account')}>
          <WalletActivityFilter />
        </Collapse.Panel>
        {/* Campaign and reservation audiences reject holdings filters. */}
        {supportsHoldings(audience) && (
          <Collapse.Panel key="holdings" header={header('holdings')}>
            <HoldingsFilter getPreviewInput={getHoldingsPreviewInput} />
          </Collapse.Panel>
        )}
        <Collapse.Panel key="location" header={header('location')}>
          {rule('location')}
          <div className={styles['invest-form']}>
            <Form.Item
              label="Country of Residence"
              name="countryOfResidence"
              tooltip="Users living in any of these countries.">
              <Select mode="multiple" style={{width}} showSearch>
                {allCountries?.map(data => (
                  <Option key={data?.name}>
                    <div className={styles['all-data']}>
                      <img src={data?.icon} alt="" />
                      <span>{data?.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="State"
              name="state"
              tooltip="Users living in any of these states. Type a state and press Enter; repeat for more.">
              <Select mode="tags" className={styles['nwidth']} showSearch>
                <Option disabled>Please Input State</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="City"
              name="city"
              tooltip="Users living in any of these cities. Type a city and press Enter; repeat for more.">
              <Select mode="tags" className={styles['nwidth']} showSearch>
                <Option disabled>Please Input City</Option>
              </Select>
            </Form.Item>
            <Form.Item
              style={{width}}
              label="Country of Birth"
              name="countryOfBirth"
              tooltip="Users born in any of these countries.">
              <Select mode="multiple" className={styles['nwidth']} showSearch>
                {allCountries?.map(data => (
                  <Option key={`${data?.name};${data?.id}`}>
                    <div className={styles['all-data']}>
                      <img src={data?.icon} alt="" />
                      <span>{data?.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              style={{width}}
              label="Country of Citizenship"
              name="countryOfCitizenship"
              tooltip="Users who are citizens of any of these countries.">
              <Select mode="multiple" className={styles['nwidth']} showSearch>
                {allCountries?.map(data => (
                  <Option key={`${data?.name};${data?.id}`}>
                    <div className={styles['all-data']}>
                      <img src={data?.icon} alt="" />
                      <span>{data?.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="U.S Visa Type"
              name="visaType"
              tooltip="Users holding any of these U.S. visa types.">
              <Select mode="tags">
                {visaTypeEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Citizenship Status"
              name="citizenStatus"
              tooltip="Users with any of these citizenship statuses.">
              <Select mode="tags">
                {citizenStatusEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Collapse.Panel>
        <Collapse.Panel key="financial" header={header('financial')}>
          {rule('financial')}
          <div className={styles['invest-form']}>
            <Form.Item
              className={styles['nwidth']}
              label="Annual Net Income"
              name="netIncome"
              tooltip="Users who reported any of these income bands.">
              <Select mode="tags">
                {annualNetIncomeEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Tax Filing Status"
              name="taxFilingStatus"
              tooltip="Users with any of these tax filing statuses.">
              <Select mode="tags">
                {taxFilingStatusEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Employment Status"
              name="employmentStatus"
              tooltip="Users with any of these employment statuses.">
              <Select mode="tags">
                {employmentStatusEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Estimated Net Worth"
              name="estimatedNetWorth"
              tooltip="Users who reported any of these net worth bands.">
              <Select mode="tags">
                {estimatedNetWorthEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Collapse.Panel>
        <Collapse.Panel key="investor" header={header('investor')}>
          {rule('investor')}
          <div className={styles['invest-form']}>
            <Form.Item
              className={styles['nwidth']}
              name="investmentExperience"
              label="Investment Experience"
              tooltip="Users who described their experience as any of these.">
              <Select mode="tags">
                {InvestingExperienceEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Preference"
              name="investmentPreference"
              tooltip="Users who chose any of these investment preferences.">
              <Select mode="tags">
                {investmentPreferenceEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Horizon"
              name="investmentHorizon"
              tooltip="Users who chose any of these investment horizons.">
              <Select mode="tags">
                {investmentHorizonEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Affiliation"
              name="investmentAffiliation"
              tooltip="Users who declared any of these affiliations.">
              <Select mode="tags">
                {investmentAffiliationEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Goals"
              name="investmentGoals"
              tooltip="Users who chose any of these goals.">
              <Select mode="tags">
                {investmentGoalsEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Dominant Investment Strategies"
              name="investmentStrategies"
              tooltip="Users whose recommended portfolio strategy is any of these.">
              <Select mode="tags">
                {investmentStrategiesEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Collapse.Panel>
        <Collapse.Panel key="signup" header={header('signup')}>
          <Form.Item
            className={styles['nwidth']}
            label="Filter by Signup Time"
            name="signupDate"
            tooltip="Only users who signed up in a date range, or within the last few days, weeks, months or years. Empty: any signup date.">
            <Select
              allowClear
              placeholder="Choose how to filter users by signup time">
              <Option value="RANGE">By Date Range (From - To)</Option>
              <Option value="DURATION">By Time Ago (e.g., Last 30 days)</Option>
            </Select>
          </Form.Item>
          {signupDate === 'RANGE' && (
            <div className={styles['schedule-grid']}>
              <Form.Item
                name="from"
                label="Signed up from"
                tooltip="Earliest signup date. Leave empty for no lower limit.">
                <DatePicker
                  className={styles.date}
                  name="From"
                  format={'YYYY-MM-DD HH:mm:ss'}
                  renderExtraFooter={() => (
                    <div style={{textAlign: 'center'}}>
                      <p>Your timezone is: {getUserTimezone()}</p>
                    </div>
                  )}
                  showTime
                />
              </Form.Item>
              <Form.Item
                name="to"
                label="Signed up until"
                tooltip="Latest signup date. Leave empty for no upper limit.">
                <DatePicker
                  className={styles.date}
                  name="To"
                  format={'YYYY-MM-DD HH:mm:ss'}
                  renderExtraFooter={() => (
                    <div style={{textAlign: 'center'}}>
                      <p>Your timezone is: {getUserTimezone()}</p>
                    </div>
                  )}
                  showTime
                />
              </Form.Item>
            </div>
          )}
          {signupDate === 'DURATION' && (
            <div className={styles['schedule-grid']}>
              <Form.Item
                name="durationUnit"
                label="Duration Unit"
                tooltip="Days, weeks, months or years. With 3 in Duration Value and Month here: users who signed up in the last 3 months."
                style={{width: 200}}
                rules={[
                  {
                    required: true,
                    message: 'Please select a time unit',
                  },
                ]}>
                <Select placeholder="Select unit" allowClear showSearch={false}>
                  <Option value="DAY">Day(s)</Option>
                  <Option value="WEEK">Week(s)</Option>
                  <Option value="MONTH">Month(s)</Option>
                  <Option value="YEAR">Year(s)</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="durationValue"
                label="Duration Value"
                tooltip="How many of the unit to look back, a whole number above 0."
                style={{width: 200}}
                rules={[
                  {
                    required: true,
                    message: 'Please enter a value',
                  },
                  ({getFieldValue}) => ({
                    validator(_, value) {
                      const durationUnit = getFieldValue('durationUnit');
                      const numValue = Number(value);
                      if (
                        durationUnit &&
                        (value === undefined ||
                          value === null ||
                          String(value).trim() === '')
                      ) {
                        throw new Error(
                          'Value is required when unit is selected',
                        );
                      }
                      if (
                        durationUnit &&
                        (!Number.isInteger(numValue) ||
                          numValue <= 0 ||
                          isNaN(numValue))
                      ) {
                        throw new Error('Value must be a positive integer');
                      }
                      // eslint-disable-next-line no-undef
                      return Promise.resolve();
                    },
                  }),
                ]}>
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g., 30"
                  disabled={!durationUnit}
                  style={{width: '100%'}}
                />
              </Form.Item>
            </div>
          )}
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

/* Asset classes that actually hold tradeable instruments. */
const ASSET_CLASS_OPTIONS = [
  {value: 'EQUITIES', label: 'Equities'},
  {value: 'FIXED_INCOMES', label: 'Fixed income'},
  {value: 'FUNDS', label: 'Funds'},
];

/* Which instrument families live under each class — the same routing the
   shared picker uses, so the two controls can never disagree. */
const INSTRUMENT_KEYS_BY_CLASS = {
  EQUITIES: [
    {value: 'PUBLIC_EQUITY', label: 'Stock / public equity'},
    {value: 'IPO', label: 'IPO'},
    {value: 'PRIVATE_EQUITY', label: 'Startup / private equity'},
  ],
  FIXED_INCOMES: [{value: 'BOND', label: 'Bond'}],
  FUNDS: [
    {value: 'MUTUAL_FUND', label: 'Mutual fund'},
    {value: 'VENTURE_FUND', label: 'Venture fund'},
  ],
};

export const NotificationOrderByFilter = ({form}) => {
  const audienceAssetClassKey = Form.useWatch('audienceAssetClassKey', form);
  const audienceInstrumentKey = Form.useWatch('audienceInstrumentKey', form);
  const audienceInstrumentId = Form.useWatch('audienceInstrumentId', form);

  // Asset class, family and instrument each take several values. The family
  // list follows every chosen class; exact instruments are searched within
  // one family, so they need exactly one family chosen.
  const chosenClasses = [].concat(audienceAssetClassKey ?? []);
  const chosenKeys = [].concat(audienceInstrumentKey ?? []);
  const chosenIds = [].concat(audienceInstrumentId ?? []);
  const instrumentKeysForClass = chosenClasses.flatMap(
    assetClass => INSTRUMENT_KEYS_BY_CLASS[assetClass] ?? [],
  );
  const singleFamily = chosenKeys.length === 1 ? chosenKeys[0] : undefined;

  return (
    <div className={styles['schedule-grid']}>
      <Form.Item
        name="orderFrom"
        label="Orders from"
        tooltip="Only orders placed on or after this date. Empty: no lower limit.">
        <DatePicker
          className={styles.date}
          format={'YYYY-MM-DD HH:mm:ss'}
          renderExtraFooter={() => (
            <div style={{textAlign: 'center'}}>
              <p>Your timezone is: {getUserTimezone()}</p>
            </div>
          )}
          showTime={{
            defaultValue: moment('00:00', 'HH:mm:ss'),
          }}
        />
      </Form.Item>
      <Form.Item
        name="orderTo"
        label="Orders until"
        tooltip="Only orders placed on or before this date. Empty: no upper limit.">
        <DatePicker
          className={styles.date}
          format={'YYYY-MM-DD HH:mm:ss'}
          renderExtraFooter={() => (
            <div style={{textAlign: 'center'}}>
              <p>Your timezone is: {getUserTimezone()}</p>
            </div>
          )}
          showTime={{
            defaultValue: moment('00:00', 'HH:mm:ss'),
          }}
        />
      </Form.Item>
      <Form.Item
        className={styles.date}
        name="orderStatus"
        label="Order Status(es)"
        tooltip="Orders in any of these statuses. Empty: any status.">
        <Select mode="multiple" placeholder="Any status" allowClear showSearch>
          {notificationOrderStatusOptions.map(option => (
            <Select.Option key={option.key}>{option.name}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        className={styles.date}
        name="orderDirection"
        label="Order Direction"
        tooltip="Buy orders, sell orders, or both. Empty: either direction.">
        <Select mode="multiple" placeholder="Any" allowClear showSearch>
          <Select.Option key="BUY">Buy</Select.Option>
          <Select.Option key="SELL">Sell</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        className={styles.date}
        name="isOrderOnHold"
        label="Order(s) On Hold"
        tooltip="Yes: only orders on hold. No: only orders not on hold. Either leaves this filter off.">
        <Select defaultValue={'EITHER'} placeholder="Either">
          {notificationFilterOptions.map(option => (
            <Option key={option.value}>
              <span>{option.label}</span>
            </Option>
          ))}
        </Select>
      </Form.Item>
      {/* <div className={styles.date} /> */}
      <Form.Item
        name="orderType"
        label="Order Type"
        tooltip="Limit orders, market orders, or both. Empty: any type."
        className={styles.date}>
        <Select mode="multiple" placeholder="Any" allowClear>
          <Option key="LIMIT">Limit</Option>
          <Option key="MARKET">Market</Option>
        </Select>
      </Form.Item>
      {/* Instrument v2 audience filters.
          `audience.assetClass` and `audience.assetId` are deprecated and
          rejected at runtime on dev. The canonical trio is asset class,
          instrument family, and exact instrument — and the notification
          service resolves the taxonomy itself from an exact instrument, so
          the admin never types a key. */}
      <Form.Item
        className={styles.date}
        name="audienceAssetClassKey"
        label="Asset class"
        tooltip="Broadest: users who ordered anything in any of these classes.">
        <Select
          mode="multiple"
          placeholder="Any asset class"
          allowClear
          onChange={nextClasses => {
            // Keep the families that still belong to a chosen class.
            const allowed = []
              .concat(nextClasses ?? [])
              .flatMap(assetClass =>
                (INSTRUMENT_KEYS_BY_CLASS[assetClass] ?? []).map(o => o.value),
              );
            const keptKeys = chosenKeys.filter(key => allowed.includes(key));
            form.setFieldsValue({
              audienceInstrumentKey: keptKeys.length ? keptKeys : undefined,
              ...(keptKeys.length === chosenKeys.length
                ? {}
                : {audienceInstrumentId: undefined}),
            });
          }}>
          {ASSET_CLASS_OPTIONS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className={styles.date}
        name="audienceInstrumentKey"
        label="Instrument family"
        tooltip="Narrower: users who ordered any of these kinds of instrument. Pick an asset class first.">
        <Select
          mode="multiple"
          placeholder="Any instrument family"
          allowClear
          disabled={!chosenClasses.length}
          onChange={() =>
            form.setFieldsValue({audienceInstrumentId: undefined})
          }>
          {instrumentKeysForClass.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className={styles.date}
        name="audienceInstrumentId"
        label="Exact instrument"
        tooltip="Narrowest: users who ordered any of these exact instruments. Pick exactly one instrument family first.">
        <FinancialInstrumentPicker
          multiple
          targetKind={singleFamily}
          value={chosenIds}
          placeholder={
            singleFamily
              ? undefined
              : chosenKeys.length > 1
              ? 'Choose one instrument family to pick exact instruments'
              : 'Pick an instrument family first'
          }
          onChange={next =>
            form.setFieldsValue({
              audienceInstrumentId: next?.length ? next : undefined,
            })
          }
        />
      </Form.Item>
    </div>
  );
};

export default NotificationFilter;
